// Share text for a solved puzzle: the puzzle with its middle words hidden, the result and a link back.
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { formatDate } from '../i18n';

export const SITE_URL = 'https://www.word-labyrinth.com';
const MAX_SHOWN_HIDDEN = 6;
const MAX_SHOWN_SECONDS = 60 * 60;
const MIN_SHOWN_STREAK = 3;

// "COLD → ❓ → ❓ → WARM": one ❓ per word between start and target, so it shows the score, not the answer.
export function puzzleLine(startWord, targetWord, steps) {
    const hidden = Math.max(0, (steps || 1) - 1);
    const middle = hidden > MAX_SHOWN_HIDDEN ? [`❓×${hidden}`] : Array(hidden).fill('❓');
    return [startWord, ...middle, targetWord].join(' → ');
}

// Quality against the shortest path, not the rank-star cap (easy dailies cap at one star).
export function shareStars(steps, minSteps) {
    if (!minSteps) return 3;
    const extra = steps - minSteps;
    return extra <= 0 ? 3 : extra === 1 ? 2 : 1;
}

export function formatDuration(seconds) {
    const s = Math.max(0, Math.round(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const rest = String(s % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${rest}` : `${m}:${rest}`;
}

export function fallbackShareUrl(mode) {
    return `${SITE_URL}/?utm_source=share&utm_medium=friend&utm_campaign=${encodeURIComponent(mode || 'game')}`;
}

function headerLabel({ mode, ladderDate }, t, language) {
    if (mode === 'ranked') {
        if (!ladderDate) return t('game.share.daily');
        const month = language === 'en' ? 'short' : 'long';
        return formatDate(`${ladderDate}T12:00:00`, { day: 'numeric', month }, language);
    }
    if (mode === 'challenge') return t('game.share.weekly');
    if (mode === 'expert') return t('game.share.expert');
    if (mode === 'practice') return t('game.share.practice');
    return '';
}

export function buildShareText(info, t, language) {
    const { mode, startWord, targetWord, steps, minSteps, elapsedSeconds, streak, url } = info;
    const perfect = !minSteps || steps <= minSteps;
    const header = ['🧩 Word Labyrinth'];
    const label = headerLabel(info, t, language);
    if (label) header.push(label);
    if (mode === 'ranked' && streak >= MIN_SHOWN_STREAK) header.push(`🔥 ${t('game.share.streak', { count: streak })}`);

    let result = t('game.share.solved', { count: steps });
    result += ` ${'⭐'.repeat(shareStars(steps, minSteps))}`;
    if (!perfect) result += ` · ${t('game.share.shortest', { count: minSteps })}`;
    if (elapsedSeconds > 0 && elapsedSeconds < MAX_SHOWN_SECONDS) result += `  ⏱ ${formatDuration(elapsedSeconds)}`;

    return [
        header.join(' · '),
        puzzleLine(startWord, targetWord, steps),
        result,
        perfect ? t('game.share.ctaPerfect') : t('game.share.ctaBeat'),
        url,
    ].join('\n');
}

// App builds from before the plugin load the same site, so check it is really there.
export function hasNativeShare() {
    try {
        return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Share');
    } catch {
        return false;
    }
}

// Native share sheet on touch devices, clipboard elsewhere. Resolves to 'shared' | 'copied' | 'cancelled' | 'failed'.
export async function deliverShareText(text, { preferSheet, dialogTitle }) {
    if (preferSheet && hasNativeShare()) {
        try {
            await Share.share({ text, dialogTitle });
            return 'shared';
        } catch (e) {
            if (/cancel/i.test(String(e?.message || e))) return 'cancelled';
        }
    }
    if (preferSheet && typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
            await navigator.share({ text });
            return 'shared';
        } catch (e) {
            if (e && e.name === 'AbortError') return 'cancelled';
        }
    }
    try {
        await navigator.clipboard.writeText(text);
        return 'copied';
    } catch {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            return ok ? 'copied' : 'failed';
        } catch {
            return 'failed';
        }
    }
}

export function prefersShareSheet(isNativeApp) {
    if (isNativeApp) return true;
    try {
        return window.matchMedia('(pointer: coarse)').matches;
    } catch {
        return false;
    }
}
